import { useRef } from 'react';
import { useUiStore, type DerivedString } from '@store/index';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

interface Props {
  s: DerivedString;
  onRetune: (pitchClass: string, octave: number, cents: number) => void;
  /** When true, dragging emits continuous cents instead of snapping to semitones. */
  freeCents: boolean;
  /** Scale degree of this string (i mod scaleSize). Used to colour-link
   *  strings that share a degree (octave copies of the same note). */
  degree: number;
  /** 0-based count of how many unique scale degrees exist — used to
   *  distribute hues evenly across them. */
  scaleSize: number;
  /** Mic-capture arming state — the string number acts as an "arm" button. */
  armed?: boolean;
  onArm?: () => void;
  /** Ruler range (MIDI) — shared across all rows of a tuning set so the
   *  scale fits without strings piling up at the edges. */
  minMidi: number;
  maxMidi: number;
}

/**
 * One row = one string on a shared pitch scale. The whole row is a track;
 * the marker sits at the string's current pitch. Drag anywhere on the
 * track to retune. Ticks + octave labels give anchors so the player
 * knows roughly where each note lives without reading numbers.
 */
export function StringRulerRow({
  s,
  onRetune,
  freeCents,
  degree,
  scaleSize,
  armed = false,
  onArm,
  minMidi,
  maxMidi,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const flashIdx = useUiStore((st) => st.flashStringIdx);
  const isFlashing = flashIdx === s.stringIndex;
  const hue = (degree * 360) / Math.max(1, scaleSize);
  // Root = yellow; Perfect 5th = red. Two distinct anchor colours so
  // the player can tell the tonic from the dominant at a glance.
  const accent = s.isRoot
    ? 'var(--key-root)'
    : s.isPerfectFifth
      ? 'var(--key-fifth)'
      : `hsl(${hue}, 68%, 60%)`;
  const TOTAL_SEMI = Math.max(12, maxMidi - minMidi);

  const totalCents = (s.midi - minMidi) * 100 + s.cents;
  const clamped = Math.max(0, Math.min(TOTAL_SEMI * 100, totalCents));
  const pct = (clamped / (TOTAL_SEMI * 100)) * 100;

  const pick = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const rawCents = (x / rect.width) * TOTAL_SEMI * 100;
    const midi = Math.min(maxMidi, Math.max(minMidi, minMidi + Math.round(rawCents / 100)));
    let cents = 0;
    if (freeCents) {
      cents = Math.max(-50, Math.min(50, rawCents - (midi - minMidi) * 100));
    }
    const pc = NOTE_NAMES[((midi % 12) + 12) % 12]!;
    const oct = Math.floor(midi / 12) - 1;
    onRetune(pc, oct, cents);
  };

  return (
    <div
      className={'str-row' + (armed ? ' str-row--armed' : '')}
      style={{ ['--accent' as string]: accent }}
    >
      <button
        type="button"
        className="str-row__num"
        onClick={onArm}
        title="Arm for mic capture"
      >
        {s.stringIndex + 1}
      </button>
      <div
        className="str-row__track"
        ref={trackRef}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId);
          pick(e.clientX);
        }}
        onPointerMove={(e) => {
          if ((e.buttons & 1) === 0) return;
          pick(e.clientX);
        }}
      >
        {/* Octave tick + label at each C in range. Starts at the first
         *  C at or above minMidi so labels line up correctly when the
         *  range doesn't start exactly on a C. */}
        {(() => {
          const firstC = minMidi + ((12 - (minMidi % 12)) % 12);
          const ticks: { m: number }[] = [];
          for (let m = firstC; m <= maxMidi; m += 12) ticks.push({ m });
          return ticks.map(({ m }) => {
            const left = ((m - minMidi) / TOTAL_SEMI) * 100;
            return (
              <div
                key={m}
                className="str-row__octave"
                style={{ left: `${left}%` }}
              >
                <div className="str-row__octave-line" />
                <div className="str-row__octave-label">C{Math.floor(m / 12) - 1}</div>
              </div>
            );
          });
        })()}
        {/* Faint semitone ticks. */}
        {Array.from({ length: TOTAL_SEMI + 1 }, (_, i) => {
          const midi = minMidi + i;
          if (midi % 12 === 0) return null;
          const left = (i / TOTAL_SEMI) * 100;
          const isSharp = [1, 3, 6, 8, 10].includes(((midi % 12) + 12) % 12);
          return (
            <div
              key={`s${i}`}
              className={
                'str-row__semi' + (isSharp ? ' str-row__semi--sharp' : '')
              }
              style={{ left: `${left}%` }}
            />
          );
        })}
        <div
          className={
            'str-row__marker' +
            (s.fromOverride ? ' str-row__marker--ov' : '') +
            (s.isRoot ? ' str-row__marker--root' : '') +
            (isFlashing ? ' str-row__marker--flash' : '')
          }
          style={{ left: `${pct}%` }}
        />
      </div>
      <div className="str-row__readout">
        <span className="str-row__note">
          {s.pitchClass}
          {s.octave}
        </span>
        {Math.abs(s.cents) >= 1 && (
          <span className="str-row__cents">
            {s.cents > 0 ? '+' : ''}
            {s.cents.toFixed(0)}¢
          </span>
        )}
      </div>
    </div>
  );
}
